/**
 * What kind of failure an error is, so each gets the right screen and the
 * right retry: stale code after a deploy or a builder's edit, a transient API
 * outage, or a real bug.
 */

/**
 * Stale code: the page asked for a module the server no longer has, or that
 * no longer has what it was asked for. A deploy replaces every chunk name (they
 * are content hashes), and a dev server mid-edit can serve a module without the
 * export its importer expects. A reload fetches a consistent set again.
 *
 * Covered: Chrome's "Failed to fetch dynamically imported module", Firefox's
 * "error loading dynamically imported module", Safari's "Importing a module
 * script failed", the linker's "does not provide an export named" (Chrome) and
 * "doesn't provide an export named" (Safari), Vite's "Unable to preload CSS",
 * and webpack-style `ChunkLoadError` / "Loading chunk 12 failed".
 */
const STALE_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /does(?: not|n't|n’t) provide an export named/i,
  /unable to preload css/i,
  /loading (?:css )?chunk [\w-]+ failed/i,
  /dynamically imported module/i,
];

export function isStaleCodeError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = String((error as { name?: unknown }).name ?? "");
  if (name === "ChunkLoadError") return true;
  const message = errorMessage(error);
  return STALE_PATTERNS.some((re) => re.test(message));
}

/** One line for a person: the error's own message, never a stack. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || error.name || "Error";
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const e = error as { message?: unknown; statusText?: unknown; status?: unknown; data?: unknown };
    if (typeof e.message === "string" && e.message) return e.message;
    // A React Router error response (a loader's `throw new Response(...)`).
    if (typeof e.status === "number") return [e.status, typeof e.statusText === "string" ? e.statusText : "", typeof e.data === "string" ? e.data : ""].filter(Boolean).join(" ");
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/** The HTTP status an API error carries, or null (a network failure, a bug). */
export function statusOf(error: unknown): number | null {
  if (error && typeof error === "object" && typeof (error as { status?: unknown }).status === "number") {
    return (error as { status: number }).status;
  }
  return null;
}

/**
 * Nothing came back at all: the API is restarting, the network dropped. `fetch`
 * rejects with a TypeError whose words differ by browser.
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  return /failed to fetch|networkerror|network error|load failed|network request failed|fetch failed/i.test(error.message);
}

/**
 * A failure that usually clears in seconds: no answer, or the API between
 * processes (502 Bad Gateway, 503 Unavailable, 504 Gateway Timeout). A 500
 * without an answer of the API's own counts too: it is what the dev and
 * preview servers' proxy sends while the API restarts.
 */
export function isTransientError(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  const status = statusOf(error);
  if (status === 502 || status === 503 || status === 504) return true;
  return status === 500 && (error as { fromServer?: unknown }).fromServer === false;
}

/** A refusal: retrying gets the same answer (auth, not found, invalid). 408 and 429 are not refusals. */
export function isRefusal(error: unknown): boolean {
  const status = statusOf(error);
  return status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429;
}
